import { AI_SOURCE_LANGUAGE } from './ai/copy.js';
/**
 * Writing style of a property (spec 20 §4 `property_writing_styles`, REQ-20-019..021):
 * fixed style rules plus up to three sample posts. The samples are STYLE
 * material only; AI contexts carry them in a section of their own and no fact
 * may ever be taken from them. API validation, the web editor and the AI
 * context builders use the same values.
 */
export const WRITING_ADDRESS_FORMS = ['du', 'sie', 'ihr', 'neutral'];
export const WRITING_EMOJI_USES = ['none', 'sparse', 'frequent'];
export const WRITING_HASHTAG_USES = ['none', 'few_at_end', 'many'];
export const WRITING_PARAGRAPH_STYLES = ['short_with_blank_lines', 'flowing'];
/**
 * Who speaks in a post (S130-15, human feedback 2026-10-01): a sole trader writes
 * "ich", a team "wir", `neutral` avoids both. Separate from `address_form` (the reader).
 */
export const WRITING_NARRATIVE_VOICES = ['first_person_singular', 'first_person_plural', 'neutral'];
/** Platforms a sample may be tagged with (a subset of the publishing platforms). */
export const WRITING_SAMPLE_PLATFORMS = ['instagram', 'facebook'];
/** Typical sentence length of the business's posts (S200-2, REQ-20-022). */
export const WRITING_SENTENCE_LENGTHS = ['short', 'medium', 'long'];
export const WRITING_HUMOURS = ['none', 'light', 'frequent'];
/** Target length of a caption (S280-4, REQ-STIL-018): the business's own choice, separate from the sentence length. */
export const WRITING_TEXT_LENGTHS = ['short', 'medium', 'long'];
/**
 * How openly a post sells (E330 S330-1, owner request 2026-10-14): `informative` informs without
 * advertising, `inviting` makes the reader want to come without asking, `persuasive` sells the
 * experience openly. Separate from `cta_mode`, which decides whether a call to action is written.
 */
export const WRITING_SELLING_TONES = ['informative', 'inviting', 'persuasive'];
/** What carries a post (E330 S330-1): `factual` facts and details, `balanced` both, `emotional` moments and feelings. */
export const WRITING_EMOTIONS = ['factual', 'balanced', 'emotional'];
/**
 * Caption length targets in characters (without hashtags and CTA), provisional (spec 80 section 4.18).
 * The format's hard limit (spec 40 section 4.3) always wins.
 */
export const TEXT_LENGTH_TARGETS = {
    short: { min: 0, max: 300 },
    medium: { min: 300, max: 700 },
    long: { min: 700, max: 1500 },
};
/** Model posts that reach an AI context at most (S280-4, spec 80 section 4.18): the first ones saved; the translator gets fewer. */
export const AI_MAX_STYLE_EXAMPLES = 5;
export const AI_MAX_STYLE_EXAMPLES_TRANSLATOR = 3;
/** Each model post in an AI context is cut at a sentence end at or below this many characters. */
export const AI_STYLE_EXAMPLE_MAX_LENGTH = 800;
/** `text` cut to at most `max` characters at the last sentence end (`.`, `!`, `?`, `…`) inside the limit; a hard cut when there is none. */
export function cutAtSentenceEnd(text, max) {
    if (text.length <= max)
        return text;
    const head = text.slice(0, max);
    const ends = [...head.matchAll(/[.!?…](?=\s|$)/gu)];
    const last = ends.at(-1);
    return (last && last.index > 0 ? head.slice(0, last.index + 1) : head).trimEnd();
}
export const WRITING_STYLE_LIMITS = {
    /** The free style note "So schreiben wir" (S280-4, REQ-STIL-017). */
    noteMaxLength: 1000,
    maxSamples: 10,
    sampleMaxLength: 2200,
    sampleSourceUrlMaxLength: 2000,
    maxOpeners: 10,
    openerMaxLength: 120,
    maxSignOffs: 10,
    signOffMaxLength: 120,
    maxFavouritePhrases: 30,
    favouritePhraseMaxLength: 80,
    maxAvoidPhrases: 50,
    avoidPhraseMaxLength: 80,
    /** Instagram allows five hashtags per caption (2026-10-12): more fixed ones could never all be used. Stored lists with more keep loading; saving more is a 422. */
    maxFixedHashtags: 5,
    signatureMaxLength: 200,
    signatureMaxLines: 3,
};
export const EMPTY_WRITING_VOICE = {
    openers: [],
    sign_offs: [],
    favourite_phrases: [],
    avoid_phrases: [],
    sentence_length: null,
    humour: null,
    fixed_hashtags: [],
    signature: null,
};
export const EMPTY_WRITING_STYLE = {
    address_form: null,
    emoji_use: null,
    hashtag_use: null,
    paragraph_style: null,
    narrative_voice: null,
    note: null,
    text_length: null,
    selling_tone: null,
    emotion: null,
    ...EMPTY_WRITING_VOICE,
    samples: [],
};
/** The language a sample without one is written in: the tenant's source language. */
export const WRITING_SAMPLE_DEFAULT_LANGUAGE = AI_SOURCE_LANGUAGE;
/**
 * What the AI may write as a call to action (S280-3, spec 20 §5.7, spec 80 §4.18).
 * Stored per property apart from the writing style; `campaign_only` is the default.
 */
export const CTA_MODES = ['never', 'campaign_only', 'always'];
export const DEFAULT_CTA_MODE = 'campaign_only';
/**
 * Style presets (E330 S330-2, owner request 2026-10-14): a starting point the editor applies to the rule
 * controls in one step, adjustable afterwards. Labels, descriptions and sample posts live in the locale files.
 */
export const WRITING_STYLE_PRESETS = {
    warm_host: { selling_tone: 'inviting', emotion: 'emotional', text_length: 'short', emoji_use: 'frequent', humour: 'light', sentence_length: 'short', paragraph_style: 'short_with_blank_lines' },
    inspiring: { selling_tone: 'persuasive', emotion: 'emotional', text_length: 'medium', emoji_use: 'sparse', humour: 'none', sentence_length: 'medium', paragraph_style: 'short_with_blank_lines' },
    informative: { selling_tone: 'informative', emotion: 'factual', text_length: 'medium', emoji_use: 'sparse', humour: 'none', sentence_length: 'medium', paragraph_style: 'flowing' },
};
export const WRITING_STYLE_PRESET_KEYS = Object.keys(WRITING_STYLE_PRESETS);
//# sourceMappingURL=writing-style.js.map